import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/contexts/WalletContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLedger } from '@/hooks/useLedger';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import { Loader2, Wallet, ExternalLink, Monitor, Smartphone, Mail, Shield, ChevronRight, Usb, Sparkles, Lock } from 'lucide-react';
import { toast } from 'sonner';

interface WalletOption {
  id: string;
  nameKey: string;
  icon: React.ReactNode;
  bgGradient: string;
  descriptionKey: string;
  isEmail?: boolean;
  badge?: string;
}

const TronLinkIcon = () => (
  <svg viewBox="0 0 32 32" className="w-7 h-7">
    <path fill="#FF0013" d="M16 2L4 10v12l12 8 12-8V10L16 2z"/>
    <path fill="#fff" d="M16 6l-8 5.5v9L16 26l8-5.5v-9L16 6zm0 3l5 3.5-5 3.5-5-3.5L16 9zm-6 5.5l5 3.5v5l-5-3.5v-5zm12 0v5l-5 3.5v-5l5-3.5z"/>
  </svg>
);

const LedgerIcon = () => (
  <svg viewBox="0 0 32 32" className="w-7 h-7">
    <rect fill="#000" width="32" height="32" rx="4"/>
    <path fill="#fff" d="M6 6h8v2H8v12h12v-6h2v8H6V6zm14 0h6v6h-2V8h-4V6zm0 8h6v12h-8v-2h6v-8h-4v-2z"/>
  </svg>
);

const EmailWalletIcon = () => (
  <div className="relative">
    <Mail className="w-7 h-7 text-white" />
    <Shield className="w-3.5 h-3.5 text-white/90 absolute -bottom-0.5 -right-0.5" />
  </div>
);

const wallets: WalletOption[] = [
  {
    id: 'email',
    nameKey: 'wallet.emailWalletName',
    icon: <EmailWalletIcon />,
    bgGradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    descriptionKey: 'wallet.emailWalletDesc',
    isEmail: true,
    badge: 'Recommended',
  },
  { 
    id: 'tronlink', 
    nameKey: 'wallet.tronlinkName',
    icon: <TronLinkIcon />,
    bgGradient: 'from-red-500 via-red-600 to-rose-600',
    descriptionKey: 'wallet.browserExtension'
  },
  { 
    id: 'ledger', 
    nameKey: 'wallet.ledgerName',
    icon: <LedgerIcon />,
    bgGradient: 'from-slate-700 via-gray-800 to-zinc-900',
    descriptionKey: 'wallet.ledgerDesc'
  },
];

interface WalletConnectModalProps {
  forceOpen?: boolean;
  hideCloseButton?: boolean;
  onClose?: () => void;
}

const WalletConnectModal = ({ forceOpen, hideCloseButton, onClose }: WalletConnectModalProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isModalOpen, closeConnectModal, connect, isConnecting, setLedgerAddress } = useWallet();
  const { user, wallet: authWallet } = useAuth();
  const ledger = useLedger();
  const [connectingWallet, setConnectingWallet] = useState<string | null>(null);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  const isOpen = forceOpen || isModalOpen;
  const handleClose = () => {
    if (hideCloseButton) return;
    onClose?.();
    closeConnectModal();
  };

  // Auto-close modal when user logs in with email wallet (no redirect)
  useEffect(() => {
    if (isModalOpen && user) {
      closeConnectModal();
    }
  }, [user, isModalOpen, closeConnectModal]);

  const handleConnect = async (walletId: string) => {
    // Email wallet - redirect to auth page
    if (walletId === 'email') {
      closeConnectModal();
      if (user && authWallet) {
        // Already logged in with email wallet - go to wallet page
        navigate('/wallet');
      } else if (user && !authWallet) {
        // Logged in but no wallet - go to setup
        navigate('/wallet-setup');
      } else {
        // Not logged in - go to auth
        navigate('/auth');
      }
      return;
    }

    // TronLink specific logic
    if (walletId === 'tronlink') {
      if (isMobile) {
        // Show toast for mobile users
        toast.info(
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 font-semibold">
              <Monitor className="w-4 h-4" />
              {t('wallet.desktopOnly', 'Desktop Only')}
            </div>
            <p className="text-sm text-muted-foreground">
              {t('wallet.tronlinkDesktopMessage', 'TronLink is available only on desktop browsers with the Chrome extension installed.')}
            </p>
          </div>,
          {
            duration: 5000,
            icon: <Smartphone className="w-5 h-5 text-primary" />,
          }
        );
        return;
      }
      
      // Check if TronLink is installed on desktop
      if (!(window as any).tronWeb && !(window as any).tronLink) {
        // Open extension page
        window.open(
          'https://chromewebstore.google.com/detail/tronlink/ibnejdfjmmkpcnlpebklmnkoeoihofec',
          '_blank'
        );
        toast.info(
          t('wallet.installTronlink', 'Please install TronLink extension and refresh the page.'),
          { duration: 4000 }
        );
        return;
      }
    }

    // Ledger hardware wallet
    if (walletId === 'ledger') {
      setConnectingWallet('ledger');
      try {
        const result = await ledger.connect();
        if (result?.address) {
          // Set the address in wallet context
          setLedgerAddress(result.address);
          toast.success(t('wallet.ledgerConnected', 'Ledger connected successfully!'));
          navigate('/wallet');
        }
      } catch (error: any) {
        toast.error(
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 font-semibold">
              <Usb className="w-4 h-4" />
              {t('wallet.ledgerError', 'Ledger Connection Failed')}
            </div>
            <p className="text-sm text-muted-foreground">
              {error.message || t('wallet.ledgerErrorMessage', 'Make sure your Ledger is connected and TRON app is open.')}
            </p>
          </div>,
          { duration: 6000 }
        );
      } finally {
        setConnectingWallet(null);
      }
      return;
    }

    setConnectingWallet(walletId);
    
    try {
      await connect(walletId);
    } catch (err: any) {
      const errorMessage = err?.message || '';
      
      if (errorMessage === 'TRONLINK_NO_WALLET') {
        toast.error(
          <div className="flex flex-col gap-2">
            <div className="font-semibold">{t('wallet.noWalletTitle', 'Wallet Not Found')}</div>
            <p className="text-sm text-muted-foreground">
              {t('wallet.noWalletMessage', 'Please create a wallet in TronLink extension first.')}
            </p>
          </div>,
          { duration: 5000 }
        );
      } else if (errorMessage === 'TRONLINK_REJECTED') {
        toast.error(t('wallet.connectionRejected', 'Connection request was rejected.'));
      } else if (errorMessage === 'TRONLINK_NOT_INSTALLED') {
        toast.info(t('wallet.installTronlink', 'Please install TronLink extension and refresh the page.'));
      } else {
        toast.error(err instanceof Error ? err.message : t('wallet.connectionFailed', 'Connection failed'));
      }
    } finally {
      setConnectingWallet(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="fixed left-0 top-0 sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] translate-x-0 translate-y-0 w-full h-[100dvh] sm:w-auto sm:h-auto sm:max-w-md sm:max-h-[90vh] max-w-none max-h-none bg-gradient-to-b from-card via-card to-card/95 backdrop-blur-xl border-0 sm:border sm:border-border/50 shadow-2xl overflow-hidden p-0 rounded-none sm:rounded-3xl [&>button]:hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
        {/* Animated background effects */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-gradient-to-b from-primary/30 via-primary/10 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-[200px] h-[200px] bg-gradient-to-tr from-emerald-500/20 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[200px] h-[200px] bg-gradient-to-tl from-cyan-500/20 to-transparent rounded-full blur-3xl" />
        </div>
        
        <div className="flex flex-col h-full sm:h-auto overflow-y-auto">
          {/* Header */}
          <div className="relative p-6 sm:p-8 pt-12 sm:pt-8">
            {/* Mobile close button - hidden when hideCloseButton is true */}
            {!hideCloseButton && (
              <button
                onClick={handleClose}
                className="sm:hidden absolute top-4 right-4 w-10 h-10 rounded-full bg-secondary/80 hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              >
                <span className="text-xl">×</span>
              </button>
            )}
            
            <DialogHeader className="space-y-4">
              {/* Animated wallet icon */}
              <div className="mx-auto relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary via-cyan-500 to-emerald-500 rounded-3xl blur-xl opacity-50 animate-pulse" />
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-gradient-to-br from-primary via-cyan-500 to-emerald-500 flex items-center justify-center shadow-2xl">
                  <Wallet className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
                  <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-yellow-400 animate-pulse" />
                </div>
              </div>
              
              <div className="text-center space-y-2">
                <DialogTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text">
                  {t('wallet.connectWallet', 'Connect Wallet')}
                </DialogTitle>
                <p className="text-sm sm:text-base text-muted-foreground">
                  {t('wallet.chooseMethod', 'Choose your preferred connection method')}
                </p>
              </div>
            </DialogHeader>
          </div>
          
          {/* Wallet List */}
          <div className="flex-1 px-4 sm:px-6 pb-6 sm:pb-8 space-y-3">
            {wallets.map((wallet, index) => (
              <div key={wallet.id} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                <button
                  type="button"
                  onClick={() => handleConnect(wallet.id)}
                  disabled={isConnecting}
                  className={`
                    w-full flex items-center gap-4 p-4 sm:p-5 rounded-2xl sm:rounded-3xl 
                    ${wallet.isEmail 
                      ? 'bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-cyan-500/15 border-2 border-emerald-500/40 hover:border-emerald-400/60 hover:from-emerald-500/20 hover:via-teal-500/15 hover:to-cyan-500/20 shadow-lg shadow-emerald-500/10' 
                      : 'bg-gradient-to-r from-secondary/90 via-secondary/70 to-secondary/50 border border-border/50 hover:border-primary/40 hover:from-secondary hover:to-secondary/70'
                    }
                    hover:shadow-xl hover:shadow-primary/10
                    transition-all duration-300 
                    disabled:opacity-50 disabled:cursor-not-allowed 
                    group
                    active:scale-[0.98]
                    relative overflow-hidden
                  `}
                >
                  {/* Shimmer effect for email option */}
                  {wallet.isEmail && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  )}
                  
                  {/* Icon */}
                  <div className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${wallet.bgGradient} flex items-center justify-center shadow-xl flex-shrink-0 group-hover:scale-105 transition-transform duration-300`}>
                    {wallet.icon}
                    {wallet.isEmail && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Lock className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-foreground font-bold text-base sm:text-lg">
                        {t(wallet.nameKey, wallet.id === 'email' ? 'Email Wallet' : wallet.id === 'walletconnect' ? 'WalletConnect' : 'TronLink')}
                      </span>
                      {wallet.badge && (
                        <span className="px-2 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                          {wallet.badge}
                        </span>
                      )}
                    </div>
                    <span className="text-muted-foreground text-xs sm:text-sm line-clamp-1">
                      {t(wallet.descriptionKey)}
                    </span>
                  </div>
                  
                  {/* Arrow/loading */}
                  {connectingWallet === wallet.id ? (
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    </div>
                  ) : wallet.isEmail ? (
                    <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                      <ChevronRight className="w-5 h-5 text-emerald-400 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                  )}
                </button>
                
                {/* Divider after email option */}
                {wallet.isEmail && (
                  <div className="flex items-center gap-3 my-5">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                    <span className="text-xs text-muted-foreground px-2">{t('wallet.orConnectWith', 'or connect with')}</span>
                    <div className="flex-1 h-px bg-gradient-to-l from-transparent via-border to-transparent" />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Terms notice */}
          <div className="px-6 pb-8 sm:pb-6 pt-2 border-t border-border/30 bg-gradient-to-t from-secondary/30 to-transparent">
            <p className="text-[11px] sm:text-xs text-muted-foreground text-center leading-relaxed">
              {t('wallet.terms', 'By connecting a wallet, you agree to the')}{' '}
              <a href="/terms" className="text-primary hover:underline font-medium">{t('wallet.termsOfUse', 'Terms of Use')}</a>
              {' '}{t('wallet.and', 'and')}{' '}
              <a href="/privacy" className="text-primary hover:underline font-medium">{t('wallet.privacyPolicy', 'Privacy Policy')}</a>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WalletConnectModal;